import { AirHandlerTypeCodeType, GridSpaceDataType } from 'data/types';
import { getAirHandlerPlacement } from 'utils/air-handler-mounting';
import { AirHandlerPlacement } from 'utils/air-handler-mounting-types';
import {
  GridPosition,
  SnapAirHandlerIntoSpaceParams
} from 'utils/air-handler-placement-types';
import {
  clampToSpace,
  snapToCenter,
  snapToInnerEdge,
  snapToZoneSpaces
} from 'utils/grid';
import { isDuctedOnlyAirHandlerCode } from 'utils/resolve-zone-type';

type PlacementSnap = (params: SnapAirHandlerIntoSpaceParams) => GridPosition;

const PLACEMENT_SNAPS: Record<AirHandlerPlacement, PlacementSnap> = {
  wall: ({ position, objectSize, space, wallSnapOptions }) => {
    const { x, y } = snapToInnerEdge(
      position,
      objectSize,
      space,
      wallSnapOptions
    );
    return { x, y };
  },
  free: ({ position, objectSize, space }) =>
    clampToSpace(position, objectSize, space),
  center: ({ position, objectSize, space, zoneSpaces = [] }) =>
    zoneSpaces.length > 1
      ? snapToZoneSpaces(position, objectSize, zoneSpaces)
      : snapToCenter(objectSize, space)
};

export function snapAirHandlerIntoSpace(
  params: SnapAirHandlerIntoSpaceParams
): GridPosition {
  return PLACEMENT_SNAPS[getAirHandlerPlacement(params.airHandlerTypeCode)](
    params
  );
}

// A ducted zone is drawn from its duct layout, whose origin only honors the
// air handler's stored position when the unit is flagged userPositioned
// (getConcealedDuctOriginOverridePx); otherwise the layout recomputes the
// zone's area-weighted center and the grid draws the unit there, whatever
// positionX/positionY say. A ducted unit is placed freely, so the spot it
// lands on IS the user's choice — flag it on every placement. Concealed-duct
// units are centered by design until the user moves one, and wall units stay
// reconciler-managed, so neither is flagged here.
export function isUserPositionedOnPlacement(
  airHandlerTypeCode: AirHandlerTypeCodeType
): boolean {
  return isDuctedOnlyAirHandlerCode(airHandlerTypeCode);
}

export function zoneGridSpaces(
  zone: { zoneItems: { structureItemId: string }[] } | null | undefined,
  gridSpaces: GridSpaceDataType[]
): GridSpaceDataType[] {
  if (!zone) return [];
  const zoneSpaceIds = new Set(
    zone.zoneItems.map((item) => item.structureItemId)
  );
  return gridSpaces.filter((gridSpace) => zoneSpaceIds.has(gridSpace.spaceId));
}
